import React, {forwardRef, useState} from 'react';

import Icon from '@pb/kit-component-icon';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './avatar.module.styl';
import {SHAPE_CLASSES, SIZE_CLASSES, Size} from './constants';

import type {SizesVariantsTypes} from './constants';
import type {IconType} from '@pb/kit-component-icon';
import type {ComponentProps, ReactNode} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export interface AvatarProps {
    /**
     * Accessible text for the avatar. Used as the `alt` attribute on the `<img>`
     * element when an image is shown. When only a placeholder is rendered, the
     * container receives `role="img"` with this value as `aria-label`. When the
     * avatar is used as a button, pass a descriptive label via `buttonProps`
     * instead to describe the action.
     */
    alt: string,
    /**
     * Content displayed when the image is unavailable — either because `src` was
     * not provided or because the image failed to load. Accepts:
     * - An `IconType` string (any value from the `Icons` object) — renders an
     *   `<Icon>` element.
     * - Any `ReactNode` — renders as-is (e.g. initials wrapped in a `<span>`).
     *
     * Image errors are caught internally; the component switches to this
     * placeholder automatically without requiring external state.
     */
    placeholder: IconType | ReactNode,
    /**
     * Size token controlling the avatar's dimensions. Accepts any key from the
     * `Size` object (e.g. `Size.NORMAL`). Defaults to `Size.NORMAL` when omitted.
     */
    size?: SizesVariantsTypes,
    /**
     * URL of the avatar image. When provided and the image loads successfully,
     * it is rendered inside an `<img>` element. When `null`, `undefined`, or when
     * the image fails to load, the component falls back to `placeholder`.
     */
    src?: string | null,
    /**
     * Additional CSS class names applied to the root element.
     */
    className?: string,
    /**
     * Border-radius shape of the avatar. Defaults to `"rounded"` (circular /
     * pill). Use `"square"` for sharp rectangular corners.
     */
    shape?: 'rounded' | 'square',
    /**
     * When `true` (default), renders a visible border ring around the avatar box.
     * Set to `false` to remove the border (e.g. when the avatar is placed on a
     * surface that provides its own visual boundary).
     */
    isBordered?: boolean,
    /**
     * Props spread directly onto the inner `<img>` element. Use for attributes
     * like `loading`, `crossOrigin`, or `decoding`. The `src`, `alt`, and
     * `onError` attributes are managed internally and will be overridden.
     */
    imgProps?: ComponentProps<'img'>,
    /**
     * Props spread onto the `<button>` element when the avatar is rendered as a
     * clickable button. If `onClick` is present in this object, the component's
     * root element switches from `<div>` to `<button>`, gaining keyboard focus
     * and accessibility semantics automatically.
     */
    buttonProps?: ComponentProps<'button'>,
}

/**
 * Displays a user or entity avatar with automatic fallback from image to
 * placeholder when the image fails to load or no `src` is provided.
 *
 * Renders as a `<div>` by default. When `buttonProps.onClick` is provided,
 * the root element becomes a `<button>` to make the avatar clickable with
 * proper keyboard and accessibility support.
 *
 * The `placeholder` prop accepts either an icon type string (renders an
 * `<Icon>`) or any `ReactNode` (e.g. initials text). Image errors are caught
 * internally and trigger an automatic fallback to the placeholder.
 *
 * ## Accessibility
 * - When showing an image: the `<img>` carries the `alt` text.
 * - When showing a placeholder without an `<img>`: the container gets
 *   `role="img"` and `aria-label` set to `alt`.
 * - When used as a button: `aria-label` should be passed via `buttonProps`.
 */
export const Avatar = forwardRef<HTMLElement, AvatarProps>((props, ref) => {
    const {
        alt,
        src,
        placeholder,
        size = Size.NORMAL,
        className,
        shape = 'rounded',
        isBordered = true,
        imgProps,
        buttonProps,
    } = props;

    const [error, setError] = useState<boolean>(false);

    const onError = () => {
        setError(true);
    };

    const isStringPlaceholder = typeof placeholder === 'string';
    const avatarClasses = classNames(
        'avatar',
        SIZE_CLASSES[size],
        SHAPE_CLASSES[shape],
        className,
        {
            avatar_bg: (error || !src) && isStringPlaceholder,
        },
    );

    const placeholderEl = isStringPlaceholder ?
            <Icon aria-hidden={false} role="img" aria-label={alt} type={placeholder} className={classes.avatar__centered}/>
        : placeholder;

    const isNotImageRole = src && !error || isStringPlaceholder;

    const avatarBox = (
        <div
            className={classNames(
                classes.avatar__box,
                {
                    avatar_border: isBordered,
                },
            )}
            role={isNotImageRole ? undefined : 'img'}
            aria-label={!isNotImageRole ? alt : undefined}>
            {src && !error
                ? <img {...{src, onError, ...imgProps}} alt={alt}/>
                : placeholderEl}
        </div>
    );

    if (buttonProps?.onClick) {
        return (
            <button
                onClick={buttonProps?.onClick}
                type="button"
                className={avatarClasses}
                {...buttonProps}
                ref={ref as React.ForwardedRef<HTMLButtonElement>}>
                {avatarBox}
            </button>
        );
    }

    return <div className={avatarClasses} ref={ref as React.ForwardedRef<HTMLDivElement>}>{avatarBox}</div>;
});

Avatar.displayName = 'Avatar';

export default Avatar;
